import { applyProgress, blankProgress, isProgress, isProgressOperation, type Progress, type ProgressOperation } from '../shared/progression';

export const PROGRESS_PREFIX = 'bg9.expedition.v2.';
export function browserStorage(): Storage | null {
  try { return localStorage; } catch { return null; }
}
interface Base { progress: Progress; revision: number; acknowledged: string[] }
interface Entry { operation: ProgressOperation; done: boolean; at: number }

/** A separate key per operation keeps two tabs from replacing each other's queue. */
export class Progression {
  private active: string;
  private syncing = new Map<string, Promise<void>>();
  private memory = new Map<string, string>();
  private storageFailed = false;

  constructor(private storage: Storage | null, legacy: Progress, private changed: () => void,
    private notice: (message: string) => void) {
    this.active = this.read(PROGRESS_PREFIX + 'active') || 'local';
    if (!this.read(PROGRESS_PREFIX + 'initialized')) {
      this.add({ id: crypto.randomUUID(), type: 'legacy', progress: isProgress(legacy) ? legacy : blankProgress() });
      this.write(PROGRESS_PREFIX + 'initialized', '1');
    }
  }

  private read(key: string): string | null {
    if (this.storageFailed && this.memory.has(key)) return this.memory.get(key)!;
    try { return this.storage?.getItem(key) ?? this.memory.get(key) ?? null; }
    catch { return this.memory.get(key) ?? null; }
  }
  private write(key: string, value: string): void {
    this.memory.set(key, value);
    try {
      if (!this.storage) throw new Error('Storage unavailable');
      this.storage.setItem(key, value);
    }
    catch {
      if (!this.storageFailed) this.notice('Browser storage is unavailable. Keep this tab open until your progress syncs.');
      this.storageFailed = true;
    }
  }
  private keys(): string[] {
    const keys = new Set(this.memory.keys());
    try { for (let i = 0; i < (this.storage?.length ?? 0); i++) { const k = this.storage!.key(i); if (k) keys.add(k); } } catch { /* memory fallback */ }
    return [...keys];
  }
  private prefix(scope = this.active): string { return PROGRESS_PREFIX + encodeURIComponent(scope) + '.'; }
  private base(scope = this.active): Base {
    try {
      const b = JSON.parse(this.read(this.prefix(scope) + 'base') || 'null') as Base;
      if (b && isProgress(b.progress) && Number.isInteger(b.revision) && Array.isArray(b.acknowledged)) return b;
    } catch { /* Older or incomplete base; the operation queue remains intact. */ }
    return { progress: blankProgress(), revision: -1, acknowledged: [] };
  }
  private entries(scope = this.active): Entry[] {
    const prefix = this.prefix(scope) + 'op.';
    return this.keys().filter(k => k.startsWith(prefix)).flatMap(k => {
      try {
        const e = JSON.parse(this.read(k) || 'null') as Entry;
        return e && isProgressOperation(e.operation) && Number.isFinite(e.at) ? [e] : [];
      } catch { return []; }
    }).sort((a, b) => a.at - b.at || a.operation.id.localeCompare(b.operation.id));
  }
  value(): Progress {
    const base = this.base();
    return this.entries().filter(e => !e.done && !base.acknowledged.includes(e.operation.id))
      .reduce((p, e) => applyProgress(p, e.operation).progress, base.progress);
  }
  add(operation: ProgressOperation): void {
    const key = this.prefix() + 'op.' + operation.id;
    if (this.read(key)) return;
    // Preserve order even when a reward and purchase happen in one millisecond.
    const at = Math.max(Date.now(), ...this.entries().map(e => e.at + 1));
    this.write(key, JSON.stringify({ operation, done: false, at } satisfies Entry));
  }
  activate(user: string | null): void {
    const scope = user ? 'user:' + user : 'local';
    if (scope === this.active) return;
    if (user && this.active === 'local') {
      for (const entry of this.entries('local')) {
        if (entry.done) continue;
        const key = this.prefix(scope) + 'op.' + entry.operation.id;
        if (!this.read(key)) this.write(key, JSON.stringify(entry));
        // Mark the source only after the destination is durable; retries reuse IDs.
        this.write(this.prefix('local') + 'op.' + entry.operation.id, JSON.stringify({ ...entry, done: true }));
      }
    }
    this.active = scope;
    this.write(PROGRESS_PREFIX + 'active', scope);
    this.changed();
  }
  sync(user: string, request: typeof fetch = fetch): Promise<void> {
    const pending = this.syncing.get(user);
    if (pending) return pending;
    const scope = 'user:' + user;
    const exchange = this.exchange(scope, user, request).finally(() => { this.syncing.delete(user); });
    this.syncing.set(user, exchange);
    return exchange;
  }
  private async exchange(scope: string, user: string, request: typeof fetch): Promise<void> {
    try {
      // A fresh device must read the account before it sends any local work.
      let response = await request('/api/expedition', { signal: AbortSignal.timeout(15_000) });
      for (let batch = 0; batch < 100; batch++) {
        if (!response.ok) throw new Error(`Sync returned HTTP ${response.status}`);
        const row = await response.json() as Progress & { userId: string; revision: number; acknowledged?: string[]; rejected?: string[] };
        if (!isProgress(row) || row.userId !== user || !Number.isInteger(row.revision)) throw new Error('Invalid account response');
        const acknowledged = row.acknowledged ?? [];
        if (row.revision >= this.base(scope).revision) {
          this.write(this.prefix(scope) + 'base', JSON.stringify({ progress: row, revision: row.revision, acknowledged }));
        }
        for (const e of this.entries(scope)) {
          if (acknowledged.includes(e.operation.id)) this.write(this.prefix(scope) + 'op.' + e.operation.id, JSON.stringify({ ...e, done: true }));
        }
        if (scope !== this.active) return;
        this.changed();
        if (row.rejected?.length) this.notice('A purchase could not sync because salvage was spent on another device. Your balance and unlocks have been updated.');
        const operations = this.entries(scope).filter(e => !e.done).slice(0, 32).map(e => e.operation);
        if (!operations.length) return;
        // No progress after a POST means contention; retry on the next sync event.
        if (batch > 0 && !acknowledged.length) throw new Error('Account is busy');
        response = await request('/api/expedition', { method: 'POST', signal: AbortSignal.timeout(15_000), headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ userId: user, operations }) });
      }
    } catch {
      if (scope === this.active) this.notice('Progress is saved on this device. Account sync will retry when you reconnect.');
    }
  }
}
